import { useMemo } from "react";
import { checkCompatibility, checkBoundaryCompatibility, checkPowerIssues, checkCablePlugCounts, checkChainCompatibility, checkSignalFanout, worstSeverity } from "../utils/compatibility";

export default function useEdgeStyling({ nodes, edges, rigCables, selection }) {
  return useMemo(() => {
    const powerIssues = checkPowerIssues(nodes, edges);
    const plugIssues = checkCablePlugCounts(edges, rigCables);

    // Check cable chain compatibility for each edge
    const chainIssues = [];
    for (const edge of edges) {
      const chain = edge.data?.cableChain;
      if (chain && chain.length > 1) {
        const chainNotes = checkChainCompatibility(chain);
        if (chainNotes.length > 0) {
          chainIssues.push({ edgeId: edge.id, notes: chainNotes });
        }
      }
    }

    const fanoutIssues = checkSignalFanout(edges);
    const allPowerIssues = [...powerIssues, ...plugIssues, ...chainIssues, ...fanoutIssues];
    const issueByEdgeId = new Map(
      allPowerIssues.filter((i) => i.edgeId).map((i) => [i.edgeId, i])
    );

    // Build edge → styling map by checking signal/boundary compatibility directly
    const edgeStyling = new Map();

    for (const edge of edges) {
      const connectionType = edge.data?.connectionType;
      if (connectionType === "power") continue; // handled via powerIssues

      const sourceNode = nodes.find((n) => n.id === edge.source);
      const targetNode = nodes.find((n) => n.id === edge.target);
      if (!sourceNode || !targetNode) continue;

      const srcIsBoundary = sourceNode.type === "signalBoundary";
      const tgtIsBoundary = targetNode.type === "signalBoundary";

      if (srcIsBoundary || tgtIsBoundary) {
        const boundaryNode = srcIsBoundary ? sourceNode : targetNode;
        const deviceNode = srcIsBoundary ? targetNode : sourceNode;
        const handleId = srcIsBoundary ? edge.targetHandle : edge.sourceHandle;
        const devicePorts = deviceNode.data?.device?.device_ports || [];
        const port = devicePorts.find((p) => String(p.id) === handleId);
        if (port) {
          const result = checkBoundaryCompatibility(boundaryNode.data.boundary, port);
          if (result.hasErrors) {
            edgeStyling.set(edge.id, { type: "warning", data: { notes: result.notes } });
          } else if (result.hasWarns) {
            edgeStyling.set(edge.id, { type: "warn", data: { notes: result.notes } });
          } else if (result.hasInfos) {
            edgeStyling.set(edge.id, { type: "info", data: { notes: result.notes } });
          }
        }
      } else if (sourceNode.type === "device" && targetNode.type === "device") {
        const sourcePorts = sourceNode.data?.device?.device_ports || [];
        const targetPorts = targetNode.data?.device?.device_ports || [];
        const fromPort = sourcePorts.find((p) => String(p.id) === edge.sourceHandle);
        const toPort = targetPorts.find((p) => String(p.id) === edge.targetHandle);
        if (fromPort && toPort) {
          const fromDevice = sourceNode.data.device;
          const toDevice = targetNode.data.device;
          const result = checkCompatibility(fromPort, toPort, {
            fromDevice, toDevice,
            cable: edge.data?.cable,
            cableChain: edge.data?.cableChain,
          });

          // Check for excluded ports
          const srcExcluded = sourceNode.data?.excludedPorts || {};
          const tgtExcluded = targetNode.data?.excludedPorts || {};
          const srcNote = srcExcluded[edge.sourceHandle];
          const tgtNote = tgtExcluded[edge.targetHandle];
          if (srcNote != null) {
            result.notes.push({ message: `Port excluded: ${srcNote || "No reason given"}`, severity: "info" });
            result.hasInfos = true;
          }
          if (tgtNote != null) {
            result.notes.push({ message: `Port excluded: ${tgtNote || "No reason given"}`, severity: "info" });
            result.hasInfos = true;
          }

          if (result.hasErrors) {
            edgeStyling.set(edge.id, { type: "warning", data: { notes: result.notes } });
          } else if (result.hasWarns) {
            edgeStyling.set(edge.id, { type: "warn", data: { notes: result.notes } });
          } else if (result.hasInfos) {
            edgeStyling.set(edge.id, { type: "info", data: { notes: result.notes } });
          } else if (fromPort.connector === "wireless" || toPort.connector === "wireless") {
            edgeStyling.set(edge.id, { type: "wireless", data: {} });
          }
        }
      }
    }

    const selectedEdgeId = selection?.type === "edge" ? selection.id : null;

    const styledEdges = edges.map((e) => {
      let styled = e;

      // Apply power issues
      const issue = issueByEdgeId.get(e.id);
      if (issue) {
        const severity = worstSeverity(issue.notes);
        const type = severity === "error" ? "warning" : severity === "warn" ? "warn" : "info";
        styled = { ...e, type, data: { ...e.data, notes: issue.notes } };
      }

      // Apply signal/boundary compatibility styling
      const signalStyling = edgeStyling.get(e.id);
      if (signalStyling) {
        styled = { ...styled, type: signalStyling.type, data: { ...styled.data, ...signalStyling.data } };
      }

      // Highlight selected edge
      const isSelected = e.id === selectedEdgeId;
      return { ...styled, selected: isSelected };
    });

    const errorEdgeCount = styledEdges.filter((e) => e.type === "warning").length;
    const powerErrorsWithoutEdge = allPowerIssues.filter(
      (i) => !i.edgeId && i.notes.some((n) => {
        const sev = typeof n === "string" ? "error" : n.severity;
        return sev === "error";
      })
    ).length;

    return {
      styledEdges,
      connectionsBadgeCount: errorEdgeCount + powerErrorsWithoutEdge,
    };
  }, [nodes, edges, rigCables, selection]);
}
